import cn from 'classnames'
import {FC, ReactElement, ReactNode} from 'react'
import Highlighter from 'react-highlight-words'

import {Text, TextVariant} from '@/components/common/content/Text'
import {Link} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type SubmenuItemProps = {
  icon?: ReactNode
  title: {text: string | ReactElement<typeof Highlighter>; textVariant?: TextVariant}
  description?: string
  linkHref: string
  prefetch?: boolean
  onClick?: () => void
  isHighlighted?: boolean
  isBulletPoint?: boolean
  hasIndent?: boolean
  textColor?: string
}

export const SubmenuItem: FC<React.PropsWithChildren<SubmenuItemProps>> = ({
  icon,
  title,
  description,
  linkHref,
  prefetch,
  onClick,
  isHighlighted,
  isBulletPoint,
  hasIndent = true,
  textColor = theme.palette.text.white,
}) => {
  const titleVariant = title.textVariant ?? TextVariant.text16

  return (
    <>
      <div className="wrapper">
        <Link
          href={linkHref}
          prefetch={prefetch}
          onClick={onClick}
          className={cn(
            'item',
            isHighlighted && 'highlighted',
            isBulletPoint && 'bullet-point',
            hasIndent && 'indent',
            description && 'has-description',
          )}
        >
          <div className="content">
            {icon && <div className="icon">{icon}</div>}
            <div>
              <Text variant={titleVariant} color={textColor}>
                <span className={cn('title', titleVariant === TextVariant.text14 && 'small')}>
                  {title.text}
                </span>
              </Text>
              {description && (
                <div className="description">
                  <Text variant={TextVariant.text14} color={theme.palette.gray[300]}>
                    {description}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Link>
      </div>

      <style jsx>{`
        .wrapper {
          height: 100%;
        }
        .wrapper :global(.item) {
          display: inline-block;
          height: 100%;
          padding: 0;
          border-radius: 5px;
          cursor: pointer;
          transition: background-color ${HOVER_TRANSITION}ms ease-in-out,
            box-shadow ${HOVER_TRANSITION}ms ease-in-out;
        }
        .wrapper :global(.item.has-description) {
          border-radius: 12px;
        }
        .wrapper :global(.item.highlighted) {
          width: 100%;
          padding: ${theme.spacing[3]};
          box-shadow: inset 0 0 0 1px ${theme.palette.gray[600]};
        }
        .wrapper :global(.item.highlighted:hover),
        .wrapper :global(.item.highlighted:focus-visible) {
          background-color: ${theme.palette.gray[800]};
          box-shadow: inset 0 0 0 1px ${theme.palette.gray[800]};
        }
        .wrapper :global(.item.indent:not(.highlighted, .bullet-point)) {
          margin-left: ${theme.spacing[3]};
        }
        .wrapper :global(.item.bullet-point) {
          display: inline-flex;
          gap: ${theme.spacing[2]};
          padding: 0;
        }
        .wrapper :global(.item.bullet-point::before) {
          content: '';
          width: 5px;
          height: 5px;
          flex: 0 0 5px;
          border-radius: 50%;
          margin-top: 10px;
          background-color: ${theme.palette.primary.main};
        }
        .content {
          display: inline-flex;
          gap: ${theme.spacing[2]};
        }
        .icon {
          margin-top: ${theme.spacing[0.5]};
        }
        .description {
          margin-top: ${theme.spacing[1.5]};
        }
        .title {
          display: inline;
          box-decoration-break: clone;
          -webkit-box-decoration-break: clone;
          background-image: repeating-linear-gradient(
              to right,
              ${theme.palette.primary.main} 0,
              ${theme.palette.primary.main} 4px,
              transparent 4px,
              transparent 8px
            ),
            linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.main});
          background-size: 8px 1px, 0% 1px;
          background-repeat: repeat-x, no-repeat;
          background-position: 0 100%, 0 100%;
          padding-bottom: ${theme.spacing[1]};
          transition: background-size ${HOVER_TRANSITION}ms ease-in-out;
        }
        .wrapper :global(.item:hover) .title,
        .wrapper :global(.item:focus-visible) .title {
          background-size: 0% 1px, 100% 1px;
        }
        .title.small {
          padding-bottom: ${theme.spacing[0.5]};
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper :global(.item:not(.highlighted, .bullet-point)) {
            margin-left: ${theme.spacing[3]};
          }
          .wrapper :global(.item.bullet-point) {
            gap: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
